import { useAuth } from "@assetwatch/auth";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useResolvedEnterpriseCustomer } from "../hooks/useResolvedEnterpriseCustomer";
import {
  getLanguagePreferenceValue,
  getSupportedLanguages,
  resolvePreferredLanguageCode,
} from "../lib/languagePreferences";

type EnterprisePendoVisitor = {
  id: string;
  email: string;
  name: string;
  role: string;
  roles: string[];
  group: string[];
  isInternalUser: boolean;
  preferred_language: string;
  sourceApp: "enterprise";
};

type EnterprisePendoAccount = {
  id: string;
  name: string;
  customerId?: number;
  externalCustomerId: string;
  facilityCount?: number;
  sourceApp: "enterprise";
};

type EnterprisePendoOptions = {
  visitor: EnterprisePendoVisitor;
  account?: EnterprisePendoAccount;
};

type EnterprisePendoApi = {
  initialize?: (options: EnterprisePendoOptions) => void;
  identify?: (options: EnterprisePendoOptions) => void;
  updateOptions?: (options: EnterprisePendoOptions) => void;
  pageLoad?: () => void;
  track?: (eventName: string, metadata?: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    pendo?: EnterprisePendoApi;
  }
}

function isLocalhost() {
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

export function EnterprisePendoProvider() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { pathname, searchStr, routeExternalCustomerId } = useRouterState({
    select: (state) => {
      const search = state.location.search as
        | { externalCustomerId?: string }
        | undefined;

      return {
        pathname: state.location.pathname,
        searchStr: state.location.searchStr,
        routeExternalCustomerId: search?.externalCustomerId,
      };
    },
  });
  const { isInternalUser, resolvedExternalCustomerId, selectableCustomers } =
    useResolvedEnterpriseCustomer(routeExternalCustomerId);
  const lastIdentityKeyRef = useRef<string | null>(null);
  const lastRouteKeyRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);
  const externalCustomerId =
    routeExternalCustomerId ?? resolvedExternalCustomerId;
  const selectedCustomer = selectableCustomers.find(
    (customer) => customer.externalCustomerId === externalCustomerId,
  );
  const pendoAccountId = selectedCustomer?.externalCustomerId;
  const pendoAccountName = selectedCustomer?.customerName;
  const pendoCustomerId = selectedCustomer?.customerId;
  const pendoFacilityCount = selectedCustomer?.facilityCount;
  const languagePreferenceValue = getLanguagePreferenceValue(user?.preferences);
  const { data: supportedLanguages = [], isPending: isLoadingLanguages } =
    useQuery({
      queryKey: ["enterprise-supported-languages"],
      queryFn: getSupportedLanguages,
      enabled:
        isAuthenticated && !isLoading && Boolean(languagePreferenceValue),
      staleTime: Infinity,
    });
  const preferredLanguage = resolvePreferredLanguageCode(
    languagePreferenceValue,
    supportedLanguages,
  );
  const isResolvingPreferredLanguage =
    Boolean(languagePreferenceValue) &&
    supportedLanguages.length === 0 &&
    isLoadingLanguages;

  useEffect(() => {
    if (
      isLoading ||
      isResolvingPreferredLanguage ||
      !isAuthenticated ||
      !user?.sub ||
      typeof window === "undefined" ||
      isLocalhost()
    ) {
      return;
    }

    const pendo = window.pendo;

    if (!pendo?.initialize) {
      return;
    }

    const account = pendoAccountId
      ? {
          id: pendoAccountId,
          name: pendoAccountName ?? pendoAccountId,
          customerId: pendoCustomerId,
          externalCustomerId: pendoAccountId,
          facilityCount: pendoFacilityCount,
          sourceApp: "enterprise" as const,
        }
      : undefined;
    const options: EnterprisePendoOptions = {
      visitor: {
        id: user.sub,
        email: user.email,
        name: user.name ?? "",
        role: user.role,
        roles: user.roles ?? [],
        group: user.cognitoGroups,
        isInternalUser,
        preferred_language: preferredLanguage,
        sourceApp: "enterprise",
      },
      account,
    };
    const identityKey = JSON.stringify(options);

    if (lastIdentityKeyRef.current === identityKey) {
      return;
    }

    lastIdentityKeyRef.current = identityKey;

    if (!hasInitializedRef.current) {
      pendo.initialize(options);
      hasInitializedRef.current = true;
      return;
    }

    if (pendo.identify) {
      pendo.identify(options);
      return;
    }

    pendo.updateOptions?.(options);
  }, [
    isAuthenticated,
    isInternalUser,
    isLoading,
    isResolvingPreferredLanguage,
    pendoAccountId,
    pendoAccountName,
    pendoCustomerId,
    pendoFacilityCount,
    preferredLanguage,
    user,
  ]);

  useEffect(() => {
    if (!hasInitializedRef.current || typeof window === "undefined") {
      return;
    }

    const routeKey = `${pathname}${searchStr}`;

    if (lastRouteKeyRef.current === routeKey) {
      return;
    }

    lastRouteKeyRef.current = routeKey;
    window.pendo?.pageLoad?.();
    window.pendo?.track?.("Enterprise Page Viewed", {
      pathname,
      search: searchStr,
      accountId: pendoAccountId ?? null,
      sourceApp: "enterprise",
    });
  }, [pendoAccountId, pathname, searchStr]);

  return null;
}
